<template>
  <div class="m-10">
    <button v-if="!externalNavigation"
        @click="showHowTo = true"
        class="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-150 border border-indigo-500 hover:border-indigo-400"
      >
        How-To User Guide
     </button>

    <HowToGuide
      v-if="isGuideVisible"
      :pages="userManagementPages"
      :initial-page="0"
      @close="handleClose"
      @complete="handleGuideComplete"
      @page-change="handlePageChange"
    />
  </div>
</template>

<script>
import { ref, computed } from "vue";
import HowToGuide from "@/components/HowToGuide.vue";
import divisionImage from "@/assets/howto/division/division.png";
import usersImage from "@/assets/howto/users/users.png";
import usersOverviewImage from "@/assets/howto/users/users-overview.png";
import usersEmailImage from "@/assets/howto/users/user-email.png";
import usersFirstnameImage from "@/assets/howto/users/users-firstname.png";
import usersLastnameImage from "@/assets/howto/users/users-lastname.png";
import usersOrgImage from "@/assets/howto/users/users-organisation.png";
import usersActiveImage from "@/assets/howto/users/users-active.png";
import usersTechnicianImage from "@/assets/howto/users/users-technician.png";
import createImage from "@/assets/howto/users/users-create-user.png";

export default {
  name: "ParentComponent",
  components: {
    HowToGuide,
  },
  
  props: {
    showHowToUser: {
      type: Boolean,
    },
    externalNavigation: {
      type: Boolean,
    }
  },

  emits: ["showHowToClose"],
  setup(props, { emit }) {
    const showHowTo = ref(false);

    const isGuideVisible = computed(() => {
      return props.externalNavigation ? props.showHowToUser : showHowTo.value;
    })

    const userManagementPages = ref([
  {
    heading: "User Management: Adding New Users",
    upperText: [
      "Administrators and users with the relevant permissions can add new users to the Mivu Platform system to grant access to the configuration interface."
    ],
    image: usersImage,
    imageAlt: "User management overview screenshot",
    lowerText: []
  },
  {
    heading: "Navigate to Users",
    upperText: [
      "How to Add a New User - Step 1"
    ],
    image: usersOverviewImage,
    imageAlt: "Users navigation screenshot",
    lowerText: [
      "From the main menu, click on \"Users\", Click \"Add User\"",
      "The \"Add New User\" form will appear"
    ]
  },
  {
    heading: "Email (Username)",
    upperText: [
      "Basic Information - Step 2"
    ],
    image: usersEmailImage,
    imageAlt: "Email username field screenshot",
    lowerText: [
      "Enter the email address of the user",
      "This will serve as their username for logging in",
      "Must be a valid email format",
      "Each email can only be used once in the system"
    ]
  },
  {
    heading: "First Name",
    upperText: [
      "Basic Information - Step 3"
    ],
    image: usersFirstnameImage,
    imageAlt: "First name field screenshot",
    lowerText: [
      "Enter the user's first name"
    ]
  },
  {
    heading: "Last Name",
    upperText: [
      "Basic Information - Step 4"
    ],
    image: usersLastnameImage,
    imageAlt: "Last name field screenshot",
    lowerText: [
      "Enter the user's last name"
    ]
  },
  {
    heading: "Organisation",
    upperText: [
      "Basic Information - Step 5"
    ],
    image: usersOrgImage,
    imageAlt: "Organisation selection screenshot",
    lowerText: [
      "Click the \"Select an organisation\" dropdown",
      "Choose which organisation this user belongs to",
      "This determines what configurations/settings a user can access"
    ]
  },
  {
    heading: "Active Status",
    upperText: [
      "User Status Settings - Step 6"
    ],
    image: usersActiveImage,
    imageAlt: "Active status checkbox screenshot",
    lowerText: [
      "Check the \"Active\" box to enable the user account",
      "Checked: User can log in and access the system",
      "Unchecked: User account is disabled and cannot log in",
      "Use this to temporarily disable users without deleting them"
    ]
  },
  {
    heading: "Technician Status",
    upperText: [
      "User Status Settings - Step 7"
    ],
    image: usersTechnicianImage,
    imageAlt: "Technician status checkbox screenshot",
    lowerText: [
      "Check \"Technician Status\" if this person is a technical user",
      "Checked: User has technician-level access and permissions",
      "Unchecked: User has standard read-only access",
      "Technicians typically have more configuration and troubleshooting capabilities"
    ]
  },
  {
    heading: "Review Information",
    upperText: [
      "Completing User Creation - Step 8"
    ],
    image: null,
    imageAlt: "Review information screenshot",
    lowerText: [
      "Double-check all entered information for accuracy",
      "Ensure the correct organisation is selected",
      "Verify the appropriate status settings"
    ]
  },
  {
    heading: "Create the User",
    upperText: [
      "Completing User Creation - Step 9"
    ],
    image: createImage,
    imageAlt: "Create user button screenshot",
    lowerText: [
      "Click the blue \"Create User\" button to save",
      "Click \"Cancel\" if you need to abort the process"
    ]
  },
  {
    heading: "Next Steps",
    upperText: [
      "What happens after user creation"
    ],
    image: null,
    imageAlt: "Next steps after user creation screenshot",
    lowerText: [
      "The new user will receive an email with a link to reset their own password (the link expires within a few days of being sent)",
      "They should log in with their new password",
      "Verify they can access the appropriate organisational configurations/settings",
      "Test their permission levels match their role requirements"
    ]
  },
  {
    heading: "User Management Best Practices",
    upperText: [
      "Guidelines for effective user management"
    ],
    image: null,
    imageAlt: "Best practices reference screenshot",
    lowerText: [
      "Technician Status Guidelines:",
      "Only grant technician status to qualified technical personnel",
      "Technicians can make configuration changes that affect monitoring",
      "Consider the user's technical expertise and job responsibilities"
    ]
  },
  {
    heading: "Troubleshooting User Issues",
    upperText: [
      "Common issues and solutions"
    ],
    image: null,
    imageAlt: "Troubleshooting guide screenshot",
    lowerText: [
      "If the User Cannot Log In:",
      "Verify the account is marked as \"Active\"",
      "Check that the email address is correct",
      "Confirm they received their login credentials",
      "Ensure they're accessing the correct system URL",
      "If the User Cannot See the Expected Configurations/Settings:",
      "Verify that the correct organisation is assigned",
      "Check that the organisation has the expected sites configured",
      "Confirm the user's permission level is appropriate"
    ]
  }
]);

    const handleGuideComplete = () => {
      console.log("User completed the guide!");
      showHowTo.value = false;
      emit("showHowToClose", false);
    };

    const handlePageChange = (pageIndex) => {
      console.log(`User navigated to page ${pageIndex + 1}`);
    };

    const handleClose = () => {
      if (props.externalNavigation){
        emit("showHowToClose", false);
      } else {
        showHowTo.value = false;
      }
    };

    return {
      showHowTo,
      userManagementPages,
      handleGuideComplete,
      handlePageChange,
      isGuideVisible,
      handleClose
    };
  }
};
</script>